import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MentionTextarea, renderWithMentions } from './mentions';
import CommentRow from './CommentRow';
import { orderByAnchors, useCommentAnchors } from './commentAnchors';

export interface ConsultationMessage {
  /** Set when the question was raised on an embedded child item, not this cycle. */
  onItem?: string | null;
  /**
   * The cycle this thread lives on — the parent's id for a question about the
   * main content, the CHILD's id for one about an embedded item. Replies must
   * go here, not to the cycle the panel happens to be rendered for.
   */
  cycleId?: string;
  commentId: string;
  at: string;
  author: { _id: string; name: string; email: string };
  issue: string;
  mentions: Array<{
    teamMemberId: string;
    name: string | null;
    email: string | null;
    notified: boolean;
  }>;
  resolved: boolean;
}

export interface ConsultationThread extends ConsultationMessage {
  sectionId: string;
  coordinates: { x: number; y: number; width?: number; height?: number } | null;
  timestamp: number | null;
  /** Layout-view crop of the marked region (data URL), roots only —
      same context finding cards show (2026-09-02). */
  anchorThumbnail?: string;
  replies: ConsultationMessage[];
}

interface ConsultationsPanelProps {
  cycleId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  /**
   * The threads, fetched by the HOST (ViewerSidePanel) rather than here.
   *
   * They moved up 2026-08-28. This panel is rendered in a ternary against the
   * comments rail and does not exist at all while the rail is collapsed, so a
   * count it owned was invisible to the tab that had to display it — which is
   * why the Consultations tab had no number while Comments did. Whoever needs
   * the count has to outlive the panel, so the fetch lives there.
   *
   * REQUIRED, all three — see the refreshKey note that used to sit here: an
   * optional data prop is one a call site can quietly omit, which is exactly
   * how the fetch-once bug survived at two of three DocumentViewer call sites.
   * Null `threads` means "not loaded yet", distinct from an empty array.
   */
  threads: ConsultationThread[] | null;
  /** Load error from the host's fetch, or null. */
  loadError: string | null;
  /** Re-run the host's fetch — called after every write this panel makes. */
  refetch: () => Promise<void>;
  /** Signed-in address — a message can be edited by its author. */
  viewerEmail?: string | null;
  /** Decision makers may edit anyone's message; mirrors PUT /comments/:id. */
  canEditAny?: boolean;
  /** A marker click asked for this thread — open its row. */
  openId?: string | null;
  onOpenIdHandled?: () => void;
}

/**
 * The consultations fetch, owned by the host so the count survives this
 * panel's unmount (see ConsultationsPanelProps.threads).
 *
 * `refreshKey` is in the callback's deps on purpose: changing it refetches.
 * The panel used to fetch once on mount and never again, so a consultation
 * posted while the cycle was open did not appear until a full reload — the
 * thread existed server-side and GET /consultations returned it, but nothing
 * re-asked (Fredrik's prod walk, 2026-08-21).
 */
export function useConsultationThreads(
  cycleId: string,
  getAuthHeaders: () => Promise<Record<string, string>>,
  refreshKey: string | number
) {
  const [threads, setThreads] = useState<ConsultationThread[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get<{ consultations: ConsultationThread[] }>(
        `${backendUrl}/api/content-review/cycles/${cycleId}/consultations`,
        { headers }
      );
      setThreads(res.data.consultations);
      setLoadError(null);
    } catch (err: any) {
      setLoadError(err.response?.data?.error || 'Failed to load consultations');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, getAuthHeaders, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await refetch(); })();
    return () => { cancelled = true; };
  }, [refetch]);

  return { threads, loadError, refetch };
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * Consultations — the cycle's open questions and their answers (spec §8,
 * 2026-08-20).
 *
 * This panel owns the consultation UI: threads are created via the composer's
 * "Ask for input", they render nowhere else (the Comment Summary filters
 * kind==='consultation' out), and they are answered and closed here. That is
 * the whole point of the split — the previous panel re-listed the same rows
 * the summary already showed, which made it read as purposeless, and the
 * mentioned expert had nowhere to actually answer.
 *
 * Anyone with cycle access can reply and can mark a thread answered — very
 * often a third party has the answer, and making the mentioned person the
 * only voice creates a bottleneck (§2.1). The backend enforces the same rule
 * (mention-fallback on the comment routes), so this UI is the affordance,
 * not the gate.
 *
 * Hidden entirely when there are no consultations — no shouty empty state.
 */
/**
 * "video embedded in: <page>" → "video". The panel lists questions about the
 * main content alongside questions about embedded items, so each row has to
 * say which it is in words the reader already uses (Fredrik, 2026-08-21).
 */
function itemKind(itemName: string): string {
  const first = (itemName || '').trim().split(/[\s:]/)[0].toLowerCase();
  return ['video', 'audio', 'image'].includes(first) ? first : 'item';
}

const ConsultationsPanel: React.FC<ConsultationsPanelProps> = ({
  cycleId, getAuthHeaders, threads, loadError, refetch, viewerEmail, canEditAny = false, openId, onOpenIdHandled
}) => {
  const { reveal, setActive, visibleCommentIds, orderedCommentIds, hasAnchor } = useCommentAnchors();
  // One thread open at a time, collapsed to a headline until then — the panel
  // shares a 360px column with everything else the cycle has to say.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // See CommentsRail: the click's own scroll would otherwise hand the row to
  // whichever thread the content passed on the way.
  const suppressSpyUntil = useRef(0);
  const [replyFor, setReplyFor] = useState<string | null>(null);
  // Editing a question or an answer, keyed by comment id (2026-08-21) — the
  // panel had no edit at all, so a typo in a question could only be answered
  // around, never corrected.
  const [editFor, setEditFor] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // A marker click in the content asks for a specific thread. Only claim it
  // if it IS one of ours — the same id may belong to a finding, which the
  // comments tab answers for.
  useEffect(() => {
    if (!openId) return;
    const mine = (threads || []).some(
      t => t.commentId === openId || t.replies.some(r => r.commentId === openId));
    if (!mine) return;
    setExpandedId(openId);
    onOpenIdHandled?.();
  }, [openId, threads, onOpenIdHandled]);

  // The reading position: the first on-screen anchor that belongs to this
  // panel. Only the first — marking every visible thread lights the whole
  // panel up on a short document and says nothing.
  //
  // Read from the VISIBLE list, not the thread list: `visibleCommentIds` is
  // in document order, so its first entry is the topmost anchor on screen.
  // Walking the threads instead picked whichever was asked first, which with
  // two anchors in view highlighted the lower one (the rail has always read
  // it this way round; this panel did not).
  const leadVisible = visibleCommentIds
    .find(id => (threads || []).some(t => t.commentId === id)) || null;

  // The reading-position row opens itself as the reader scrolls (Fredrik,
  // 2026-08-26), so the thread about the passage under their eye is the one
  // they can read.
  useEffect(() => {
    if (!leadVisible) return;
    if (Date.now() < suppressSpyUntil.current) return;
    setExpandedId(leadVisible);
  }, [leadVisible]);

  // Mark the open thread's anchor in the content, so the pair reads as one
  // thing (Fredrik, 2026-08-26). Cleared on unmount — a tab switch must not
  // strand a highlight.
  useEffect(() => {
    if (!(threads || []).some(t => t.commentId === expandedId)) return;
    setActive(expandedId);
  }, [expandedId, threads, setActive]);

  useEffect(() => () => setActive(null), [setActive]);

  // A click always takes you to the passage and never closes — same rule as
  // the rail's, and for the same reason: the spy opens the reading-position
  // thread by itself, so a click read as "close" scrolled nowhere. See
  // CommentsRail.toggle. reveal() is out of the setState updater too.
  const toggleThread = (id: string) => {
    suppressSpyUntil.current = Date.now() + 900;
    reveal(id);
    setExpandedId(id);
  };

  if (loadError) return null;         // silent when the endpoint fails

  // 2026-08-21 (Fredrik) — the panel used to disappear entirely when empty,
  // so the one place that tracks outstanding asks was invisible precisely
  // when someone was looking for how to make one. It is now a permanent area
  // that explains itself.
  // Threads read in the order their anchors appear in the content, not the
  // order they were asked (2026-08-26, Fredrik) — same reason as the rail's.
  const list = orderByAnchors(threads || [], t => t.commentId, orderedCommentIds);
  const open = list.filter(t => !t.resolved).length;

  // Is this question addressed to the reader? A mention on an embedded item
  // is easy to miss: it renders on that item's tab, so someone who lands on
  // the main tab sees no cue at all that they have been asked something
  // (Fredrik, 2026-08-21 — he replied to one only because he went looking).
  // The panel lists both levels, so it is the right place to say so.
  const mentionsMe = (t: ConsultationMessage) =>
    !!viewerEmail && (t.mentions || []).some(m =>
      (m.email || '').toLowerCase() === viewerEmail.toLowerCase());
  const awaitingMe = list.filter(t => !t.resolved && mentionsMe(t)).length;

  function formatTime(iso: string) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  const postReply = async (rootId: string, threadCycleId?: string) => {
    const text = replyText.trim();
    if (!text) return;
    setBusy(true);
    setActionError(null);
    try {
      const headers = await getAuthHeaders();
      // Replies inherit the root's kind server-side, so this lands as part
      // of the consultation thread.
      // The thread's OWN cycle, not the panel's. A question about the
      // embedded video lives on the child cycle, and POST /comments refuses a
      // parentComment from a different cycle — so replying to a child thread
      // from the parent's panel failed with PARENT_CYCLE_MISMATCH until this
      // carried the right id (Fredrik, 2026-08-21).
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${threadCycleId || cycleId}/comments`,
        { issue: text, parentComment: rootId },
        { headers }
      );
      setReplyText('');
      setReplyFor(null);
      await refetch();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Could not post the reply.');
    } finally {
      setBusy(false);
    }
  };

  // Author or DM, matching the backend rule on PUT /comments/:id. Editing a
  // consultation re-resolves its mentions server-side, so adding an @handle
  // here pulls that person in and notifies them — the same as asking at
  // compose time.
  const canEdit = (authorEmail?: string) =>
    canEditAny || (!!viewerEmail && !!authorEmail &&
      authorEmail.toLowerCase() === viewerEmail.toLowerCase());

  const saveEdit = async (commentId: string) => {
    const text = editText.trim();
    if (!text) return;
    setBusy(true);
    setActionError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${backendUrl}/api/content-review/comments/${commentId}`,
        { issue: text },
        { headers }
      );
      setEditFor(null);
      setEditText('');
      await refetch();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Could not save the edit.');
    } finally {
      setBusy(false);
    }
  };

  // `review-form-group` is the shared field style the composer got on
  // 2026-08-26 (white ground against the panel, hover border, a real 3px focus
  // ring). The edit and reply boxes were left on their own inline styles that
  // day, so the one form a reviewer meets while ANSWERING looked nothing like
  // the one they met while asking.
  // Delete is AUTHOR-only (Fredrik, 2026-09-02) — narrower than canEdit,
  // which canEditAny holders (DM/admin views) get for editing. The backend
  // enforces the same rule plus thread guards: a root with replies comes
  // back 409 "resolve instead", surfaced via actionError.
  const deleteMessage = async (commentId: string) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    setBusy(true);
    setActionError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${backendUrl}/api/content-review/comments/${commentId}`, { headers });
      setEditFor(null);
      setEditText('');
      await refetch();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  const editBox = (commentId: string, authorEmail?: string) => (
    <div data-testid="consultation-edit-form">
      <div className="review-form-group">
        <MentionTextarea
          value={editText}
          onChange={setEditText}
          getAuthHeaders={getAuthHeaders}
          placeholder="Edit — @mention anyone you also need"
          rows={3}
        />
      </div>
      <div className="comment-reply-actions">
        <button
          className="review-btn review-btn-primary rail-btn"
          onClick={() => saveEdit(commentId)}
          disabled={!editText.trim() || busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="comment-reply-open rail-action-muted"
          onClick={() => { setEditFor(null); setEditText(''); }}>
          Cancel
        </button>
        {!!viewerEmail && !!authorEmail && authorEmail.toLowerCase() === viewerEmail.toLowerCase() && (
          <button
            className="comment-reply-open rail-action-muted"
            style={{ marginLeft: 'auto', color: '#b91c1c' }}
            disabled={busy}
            onClick={() => deleteMessage(commentId)}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );

  const setAnswered = async (rootId: string, answered: boolean) => {
    setBusy(true);
    setActionError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${backendUrl}/api/content-review/comments/${rootId}`,
        { resolved: answered },
        { headers }
      );
      await refetch();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Could not update the thread.');
    } finally {
      setBusy(false);
    }
  };

  const anchorLabel = (t: ConsultationThread): string | null => {
    if (t.timestamp !== null && t.timestamp !== undefined) {
      const m = Math.floor(t.timestamp / 60);
      const s = String(Math.round(t.timestamp % 60)).padStart(2, '0');
      return `at ${m}:${s}`;
    }
    if (t.coordinates) return t.coordinates.width ? 'on a marked area' : 'on a pinned spot';
    if (t.sectionId) return 'on the content';
    return null;
  };

  return (
    <div data-testid="consultations-panel" className="rail-section">
      <div className="rail-section-head">
        <h4 className="rail-section-title">Consultations</h4>
        <span className="rail-section-counts">
          {open} open · {list.length - open} answered
        </span>
        {awaitingMe > 0 && (
          <span data-testid="consultations-awaiting-you" className="rail-badge-mention">
            {awaitingMe} awaiting you
          </span>
        )}
      </div>

      {/* The panel used to disappear when empty, so the one place that tracks
          outstanding asks was invisible precisely when someone went looking
          for how to make one (2026-08-21). It explains itself instead — but
          only when there is nothing to read, because in a 360px column the
          explanation would otherwise push the threads off the screen. */}
      {list.length === 0 && (
        <p data-testid="consultations-empty" className="rail-empty">
          No questions asked yet. To start one, select or pin the place it concerns
          and choose <strong>Ask for input</strong>, then @mention who you are asking —
          that mention is what notifies them and gives them access to this review.
        </p>
      )}

      {actionError && <p className="rail-error">{actionError}</p>}

      <div className="rail-list">
        {list.map(t => {
          const forMe = mentionsMe(t);
          const editing = editFor === t.commentId;
          return (
            <CommentRow
              key={t.commentId}
              commentId={t.commentId}
              author={t.author.name}
              at={t.at}
              body={t.issue}
              accent={t.resolved ? '#1e6b3a' : '#7c3aed'}
              forMe={forMe}
              active={t.commentId === leadVisible}
              hasAnchor={hasAnchor(t.commentId)}
              expanded={expandedId === t.commentId}
              onToggle={() => toggleThread(t.commentId)}
              badges={
                <>
                  {t.onItem && (
                    <span data-testid="consultation-on-item" className="rail-badge-item"
                      title={`This question is about ${t.onItem}. You can answer it from here.`}>
                      on the {itemKind(t.onItem)}
                    </span>
                  )}
                  {forMe && (
                    <span data-testid="consultation-mentions-you" className="rail-badge-mention"
                      title={t.resolved ? 'You were asked this' : 'You were asked this — a reply is expected'}>
                      {t.resolved ? 'Mentioned you' : 'Mentions you'}
                    </span>
                  )}
                  {t.resolved
                    ? <span className="rail-badge-resolved">Answered</span>
                    : <span className="rail-badge-open">Awaiting answer</span>}
                  {t.replies.length > 0 && (
                    <span className="rail-badge-replies">{t.replies.length} repl{t.replies.length === 1 ? 'y' : 'ies'}</span>
                  )}
                </>
              }
            >
              {anchorLabel(t) && (
                <p className="rail-row-anchor">asked {anchorLabel(t)}</p>
              )}

              {t.anchorThumbnail && (
                <img
                  src={t.anchorThumbnail}
                  alt="Marked region"
                  style={{ maxWidth: '100%', maxHeight: 80, display: 'block', border: '1px solid #ddd', borderRadius: 3, marginBottom: 4 }}
                />
              )}

              {editing ? editBox(t.commentId, t.author?.email) : (
                <p className="rail-row-text">
                  {renderWithMentions(t.issue, t.mentions.map(m => ({
                    teamMemberId: m.teamMemberId, name: m.name, email: m.email
                  })))}
                </p>
              )}

              {t.replies.length > 0 && (
                <div className="comment-thread">
                  {t.replies.map(r => (
                    <div key={r.commentId} id={`comment-${r.commentId}`} className="comment-thread-item">
                      <div className="comment-thread-head">
                        <span className="comment-thread-author">{r.author.name}</span>
                        <span className="comment-thread-time">{formatTime(r.at)}</span>
                      </div>
                      {editFor === r.commentId ? editBox(r.commentId, r.author?.email) : (
                        <p className="comment-thread-body">
                          {renderWithMentions(r.issue, r.mentions.map(m => ({
                            teamMemberId: m.teamMemberId, name: m.name, email: m.email
                          })))}
                        </p>
                      )}
                      {canEdit(r.author?.email) && editFor !== r.commentId && (
                        <button
                          className="comment-reply-open"
                          data-testid="consultation-edit-reply"
                          onClick={() => { setEditFor(r.commentId); setEditText(r.issue); }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="comment-reply-actions">
                {replyFor !== t.commentId && (
                  <button className="comment-reply-open" onClick={() => { setReplyFor(t.commentId); setReplyText(''); }}>
                    Reply
                  </button>
                )}
                {canEdit(t.author?.email) && !editing && (
                  <button
                    className="comment-reply-open"
                    data-testid="consultation-edit-root"
                    onClick={() => { setEditFor(t.commentId); setEditText(t.issue); }}
                  >
                    Edit
                  </button>
                )}
                {t.resolved ? (
                  <button className="comment-reply-open rail-action-muted"
                    onClick={() => setAnswered(t.commentId, false)} disabled={busy}>
                    Reopen
                  </button>
                ) : (
                  <button className="comment-reply-open rail-action-done"
                    onClick={() => setAnswered(t.commentId, true)} disabled={busy}
                    title="Close this thread — the question has been answered">
                    Mark answered
                  </button>
                )}
              </div>

              {replyFor === t.commentId && (
                <div className="comment-reply-box">
                  <div className="review-form-group">
                    <MentionTextarea
                      value={replyText}
                      onChange={setReplyText}
                      getAuthHeaders={getAuthHeaders}
                      placeholder="Write your answer… use @name to pull someone in"
                      rows={3}
                    />
                  </div>
                  <div className="comment-reply-actions">
                    <button
                      className="review-btn review-btn-primary rail-btn"
                      onClick={() => postReply(t.commentId, t.cycleId)}
                      disabled={busy || !replyText.trim()}
                    >
                      {busy ? 'Posting…' : 'Post answer'}
                    </button>
                    <button className="comment-reply-open rail-action-muted"
                      onClick={() => { setReplyFor(null); setReplyText(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </CommentRow>
          );
        })}
      </div>
    </div>
  );
};

export default ConsultationsPanel;
